import { createPortal } from 'react-dom';
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  useCallback,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { AmbientParticles } from '@src/theme/Decoration';
import { useTheme } from '@src/theme/useTheme';
import { scopeSvg } from '@src/theme/svg';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import type { LandscapeLayerDefinition } from '@src/theme/types';
import './Landscape.css';
import { useCounterGroundAnchor } from './useCounterGroundAnchor';

export interface LandscapeProps {
  /** Blur scenery behind forms without stopping its ambient animation. */
  appearance?: 'clear' | 'soft';
  parallax?: boolean;
  foreground?: ReactNode;
  overlay?: ReactNode;
  foregroundPlacement?: 'default' | 'above-navigation';
  className?: string;
  testId?: string;
}

function LandscapeLayer({
  layer,
  children,
}: {
  layer: LandscapeLayerDefinition;
  children?: ReactNode;
}) {
  const { theme } = useTheme();
  const raster = layer.raster?.[theme];
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const svg = useMemo(
    () => scopeSvg(layer.svg, `landscape-${instanceId}-${layer.id}`),
    [layer.svg, layer.id, instanceId],
  );
  const [embeddedSlot, setEmbeddedSlot] = useState<HTMLElement | null>(null);
  const bindArtwork = useCallback((node: HTMLDivElement | null) => {
    setEmbeddedSlot(node?.querySelector<HTMLElement>('.landscape__embedded-counter') ?? null);
  }, []);
  const embedsCounter = layer.svg.includes('data-landscape-counter-slot');
  const style = {
    '--layer-width': `${layer.width}px`,
    '--layer-height': `${layer.height}px`,
    '--layer-x': `${layer.x}px`,
    '--layer-bottom': `${layer.bottom}px`,
    '--layer-speed': layer.speed,
  } as CSSProperties;

  return (
    <div
      className={classes(
        'landscape__layer',
        `landscape__layer--${layer.anchor}`,
        `landscape__layer--${layer.visibility}`,
        layer.theme && `landscape__layer--${layer.theme}`,
      )}
      data-landscape-layer={layer.id}
      data-landscape-slot={layer.slot}
      data-landscape-has-foreground={children ? true : undefined}
      style={style}
    >
      <div className="landscape__motion">
        {/* Only repository-owned Figma assets enter this markup; no runtime/user HTML. */}
        {raster ? (
          <div className="landscape__art landscape__art--raster" aria-hidden="true">
            {(layer.rasterRepeatX ? [-1, 0] : [0]).flatMap((tile) =>
              raster.flatMap((part, index) =>
                (part.stillSrc ? [part.src, part.stillSrc] : [part.src]).map((src, variant) => {
                  const image = <img
                    key={`${src}:${index}:${tile}`}
                    className={classes(
                      'landscape__raster',
                      part.stillSrc &&
                        (variant ? 'landscape__raster--still' : 'landscape__raster--animated'),
                    )}
                    src={src}
                    // Wait until the picture sources are attached before fetching a fallback.
                    loading={part.sources ? 'lazy' : undefined}
                    alt=""
                    draggable={false}
                    decoding="async"
                    style={{
                      left: `${part.bounds[0] + tile * 100}%`,
                      top: `${part.bounds[1]}%`,
                      width: `${part.bounds[2]}%`,
                      height: `${part.bounds[3]}%`,
                    }}
                  />;
                  return part.sources ? <picture key={`${src}:${index}:${tile}`}>
                    {part.sources.map(source => <source key={source.media} media={source.media} srcSet={source.srcSet} />)}
                    {image}
                  </picture> : image;
                }),
              ),
            )}
          </div>
        ) : (
          <div
            ref={bindArtwork}
            aria-hidden={embedsCounter && children ? undefined : true}
            className={classes('landscape__art', layer.flipY && 'landscape__art--flip-y')}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
        {children &&
          (embedsCounter ? (
            embeddedSlot && createPortal(children, embeddedSlot)
          ) : (
            <div className="landscape__foreground-content">{children}</div>
          ))}
      </div>
    </div>
  );
}

/** One decorative scene shared by all routes. Artwork stays at its native size. */
export function Landscape({
  appearance = 'clear',
  parallax = true,
  foreground,
  overlay,
  foregroundPlacement = 'default',
  className,
  testId = 'landscape',
}: LandscapeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { seasonPackage, theme } = useTheme();
  useCounterGroundAnchor(ref, seasonPackage.id);

  useEffect(() => {
    const node = ref.current;
    if (!node || !parallax) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let target = 0;
    let current = 0;
    let lastTime = 0;
    const smooth = seasonPackage.id === 'alpine';
    const movingLayers = smooth ? seasonPackage.landscape.layers.flatMap(layer => {
      if (!layer.speed) return [];
      const motion = node.querySelector<HTMLElement>(`[data-landscape-layer="${layer.id}"] > .landscape__motion`);
      return motion ? [{ motion, speed: layer.speed }] : [];
    }) : [];
    let needsMeasurement = false;

    function update(time = performance.now()) {
      frame = 0;
      if (!node) return;
      if (needsMeasurement) {
        const bounds = node.getBoundingClientRect();
        target = reducedMotion.matches ? 0 : Math.min(bounds.height, Math.max(0, -bounds.top));
        needsMeasurement = false;
      }
      const dt = lastTime ? Math.min(64, time - lastTime) : 16;
      lastTime = time;
      current = smooth && !reducedMotion.matches
        ? current + (target - current) * (1 - Math.exp(-dt / 85))
        : target;
      if (Math.abs(target - current) < 0.1) current = target;
      if (smooth) {
        for (const { motion, speed } of movingLayers) {
          motion.style.transform = `translate3d(0, ${(current * speed).toFixed(2)}px, 0)`;
        }
      } else {
        node.style.setProperty('--landscape-scroll', `${current.toFixed(2)}px`);
      }
      if (current !== target) frame = window.requestAnimationFrame(update);
      else lastTime = 0;
    }

    function schedule() {
      if (!node) return;
      needsMeasurement = true;
      if (!frame) frame = window.requestAnimationFrame(update);
    }

    function syncMotionPreference() {
      window.removeEventListener('scroll', schedule, true);
      if (!reducedMotion.matches)
        window.addEventListener('scroll', schedule, { passive: true, capture: true });
      window.cancelAnimationFrame(frame);
      frame = 0;
      schedule();
    }

    syncMotionPreference();
    window.addEventListener('resize', schedule);
    reducedMotion.addEventListener('change', syncMotionPreference);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      reducedMotion.removeEventListener('change', syncMotionPreference);
      node.style.removeProperty('--landscape-scroll');
      for (const { motion } of movingLayers) motion.style.removeProperty('transform');
    };
  }, [parallax, seasonPackage.id, seasonPackage.landscape.layers, theme]);

  return (
    <div
      ref={ref}
      data-landscape-composition="shared"
      className={classes(
        'landscape',
        `landscape--${appearance}`,
        foregroundPlacement === 'above-navigation' && 'landscape--foreground-above-navigation',
        className,
      )}
      {...testIdAttr(testId)}
    >
      <div className="landscape__artwork">
        {seasonPackage.landscape.layers.filter(layer =>
          seasonPackage.id !== 'alpine' || !layer.theme || layer.theme === (theme === 'dark' ? 'night' : 'day'),
        ).map((layer) => (
          <LandscapeLayer key={`${layer.id}-${layer.theme ?? 'shared'}`} layer={layer}>
            {layer.slot === 'counter' ? foreground : null}
          </LandscapeLayer>
        ))}
      </div>
      <AmbientParticles />
      <div className="landscape__fade" />
      {overlay}
    </div>
  );
}
